import React, { useState, useEffect, useCallback } from 'react';
import {
  Inbox, DollarSign, AlertTriangle, CheckCircle, ExternalLink, Clock,
  Send, FileText, RefreshCw, Download, Phone, Mail, Inbox as InboxIcon,
  MessageSquare, Building2, Check, X as XIcon,
} from 'lucide-react';
import { authFetch, API } from './api';

/*
 * Operator console for self-serve athlete profiles.
 *
 * Invoicing happens in Bluevine / Venmo; this records what the operator did and
 * tells them who to chase. Amount columns say "recorded" rather than "paid"
 * because nothing here is confirmed by a payment processor.
 */

const money = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);
const day = (d) => (d ? new Date(d + (String(d).length === 10 ? 'T00:00:00' : '')).toLocaleDateString() : '—');

const BUCKETS = [
  ['current', 'Current', '#546E7A'],
  ['1_30', '1–30 days', '#FFD600'],
  ['31_60', '31–60 days', '#FFB300'],
  ['61_90', '61–90 days', '#FF7043'],
  ['over_90', '90+ days', '#EF5350'],
];

const Tile = ({ label, value, color }) => (
  <div className="panel-card text-center py-4">
    <div className="font-rajdhani font-bold text-2xl md:text-3xl leading-none" style={{ color }}>{value}</div>
    <div className="text-[#546E7A] text-[10px] uppercase tracking-[0.1em] mt-1.5 font-semibold">{label}</div>
  </div>
);

const Listings = () => {
  const [tab, setTab] = useState('queue');
  const [queue, setQueue] = useState([]);
  const [report, setReport] = useState(null);
  const [aging, setAging] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');

  const flash = (m) => { setStatus(m); setTimeout(() => setStatus(''), 2800); };

  const load = useCallback(async () => {
    const [q, r, a, i] = await Promise.all([
      authFetch(`${API}/api/listings/queue`),
      authFetch(`${API}/api/listings/billing-report`),
      authFetch(`${API}/api/listings/aging`),
      authFetch(`${API}/api/inquiries`),
    ]);
    if (q.ok) setQueue(await q.json());
    if (r.ok) setReport(await r.json());
    if (a.ok) setAging(await a.json());
    if (i.ok) setInquiries(await i.json());
  }, []);

  useEffect(() => { load(); }, [load]);

  const post = async (path, body) => {
    setBusy(true);
    try {
      const r = await authFetch(`${API}/api/listings/${path}`, {
        method: 'POST',
        body: JSON.stringify(body || {}),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      await load();
      return true;
    } catch (e) { flash(String(e.message || e)); return false; }
    finally { setBusy(false); }
  };

  const recordInvoice = async (item) => {
    const amount = window.prompt(`Invoice amount for ${item.athlete}:`, item.suggested_amount);
    if (amount === null) return;
    const method = window.prompt('Method (bluevine / venmo / check / cash / other):', 'bluevine');
    if (method === null) return;
    const reference = window.prompt('Invoice number or reference (optional):') || null;
    if (await post(`${item.player_id}/billing`, {
      event_type: 'invoice_sent', amount: parseFloat(amount), method, reference,
    })) flash('Invoice recorded ✓');
  };

  const recordPayment = async (item) => {
    const amount = window.prompt(`Payment received from ${item.guardian_name || item.athlete}:`,
      item.suggested_amount ?? item.balance);
    if (amount === null) return;
    const method = window.prompt('Method (bluevine / venmo / check / cash / other):', 'venmo');
    if (method === null) return;
    const reference = window.prompt('Reference (optional):') || null;
    if (await post(`${item.player_id}/billing`, {
      event_type: 'payment_received', amount: parseFloat(amount), method, reference,
    })) flash('Payment recorded ✓');
  };

  const approve = async (item) => {
    if (!window.confirm(`Publish ${item.athlete}'s page and start their term?`)) return;
    if (await post(`${item.player_id}/approve`, {})) flash('Published ✓');
  };

  const sendBack = async (item) => {
    const note = window.prompt('What needs changing? (shown to the guardian)');
    if (!note) return;
    if (await post(`${item.player_id}/request-changes`, { note })) flash('Sent back ✓');
  };

  const setInquiryStatus = async (item, status) => {
    setBusy(true);
    try {
      await authFetch(`${API}/api/inquiries/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      await load();
      flash(status === 'contacted' ? 'Marked contacted ✓' : 'Closed ✓');
    } finally { setBusy(false); }
  };

  const addInquiryNote = async (item) => {
    const note = window.prompt('Note on this inquiry:', item.note || '');
    if (note === null) return;
    await authFetch(`${API}/api/inquiries/${item.id}`, {
      method: 'PATCH', body: JSON.stringify({ note }),
    });
    await load();
    flash('Note saved ✓');
  };

  const exportCsv = (rows, name) => {
    if (!rows?.length) return;
    const cols = Object.keys(rows[0]);
    const csv = [cols.join(',')].concat(
      rows.map((r) => cols.map((c) => `"${String(r[c] ?? '').replace(/"/g, '""')}"`).join(','))
    ).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const TABS = [
    ['queue', 'Queue', queue.length],
    ['billing', 'Invoices', report?.due_to_invoice?.length || 0],
    ['aging', 'Aging', (aging?.past_term_in_grace?.length || 0)],
    ['inquiries', 'Inquiries', inquiries.filter((x) => x.status === 'new').length],
  ];

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-6">
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-12">

        <div className="flex items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Billing</h1>
            <p className="text-[#546E7A] text-xs md:text-sm mt-1">
              Athlete profiles — review, invoice, publish, and chase what's owed.
            </p>
          </div>
          <button onClick={load} className="flex items-center gap-2 px-3 py-2 border border-[#546E7A]/40 text-[#546E7A] rounded-lg text-sm">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {status && <div className="text-[#00E676] text-sm font-semibold mb-3">{status}</div>}

        {report && (
          <div className="grid grid-cols-2 xs:grid-cols-4 gap-3 mb-5">
            <Tile label="Awaiting review" value={queue.length} color="#FFB300" />
            <Tile label="New inquiries" value={inquiries.filter((x) => x.status === 'new').length} color="#7E57C2" />
            <Tile label="Self-paying" value={report.summary.self_paying} color="#00E676" />
            <Tile label="School-covered" value={report.summary.covered_by_school} color="#00D4FF" />
            <Tile label="Run rate / yr" value={money(report.summary.annual_run_rate)} color="#00D4FF" />
            <Tile label="Outstanding" value={money(report.summary.outstanding_total)} color="#EF5350" />
            {report.summary.school_lapsed > 0 && (
              <Tile label="School lapsed" value={report.summary.school_lapsed} color="#FF7043" />
            )}
          </div>
        )}

        <div className="chip-row md:flex md:gap-2 mb-5">
          {TABS.map(([id, label, count]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`chip ${tab === id ? 'is-active' : ''}`}>
              {label}{count ? ` (${count})` : ''}
            </button>
          ))}
        </div>

        {/* ── Queue ─────────────────────────────────────────── */}
        {tab === 'queue' && (
          queue.length === 0 ? (
            <div className="panel-card text-center py-12 text-[#546E7A]">
              Nothing waiting on you.
            </div>
          ) : (
            <div className="space-y-3">
              {queue.map((item) => (
                <div key={`${item.type}-${item.id}`} className="panel-card">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] font-bold rounded px-1.5 py-0.5 border"
                          style={item.type === 'inquiry'
                            ? { color: '#7E57C2', borderColor: '#7E57C2' }
                            : { color: '#FFB300', borderColor: '#FFB300' }}>
                          {item.type === 'inquiry'
                            ? `${(item.kind || 'general').toUpperCase()} SIGNUP`
                            : 'ATHLETE PROFILE'}
                        </span>
                        {item.days_waiting != null && (
                          <span className="text-[#546E7A] text-xs flex items-center gap-1">
                            <Clock size={11} /> {item.days_waiting}d
                          </span>
                        )}
                      </div>

                      {item.type === 'inquiry' ? (
                        <>
                          <div className="text-white font-bold">
                            {item.organization || item.contact_name}
                          </div>
                          <div className="text-[#B0BEC5] text-xs mt-1">
                            {item.contact_name}{item.role ? ` · ${item.role}` : ''}{item.state ? ` · ${item.state}` : ''}
                          </div>
                          <div className="flex flex-wrap gap-3 mt-2">
                            <a href={`mailto:${item.email}`} className="flex items-center gap-1 text-xs">
                              <Mail size={11} /> {item.email}
                            </a>
                            {item.phone && (
                              <a href={`tel:${item.phone}`} className="flex items-center gap-1 text-xs">
                                <Phone size={11} /> {item.phone}
                              </a>
                            )}
                          </div>
                          {item.message && (
                            <p className="text-[#C8D8E8] text-sm mt-2.5 leading-relaxed whitespace-pre-line">
                              {item.message}
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="text-white font-bold">{item.athlete}</div>
                          <div className="text-[#B0BEC5] text-xs mt-1 flex flex-wrap items-center gap-1.5">
                            <span className="text-[#00D4FF] font-semibold">{item.position}</span>
                            {item.graduation_year && <span>• Class of {item.graduation_year}</span>}
                            {item.high_school && <span>• {item.high_school}</span>}
                          </div>
                          <div className="text-[#546E7A] text-xs mt-2">
                            {item.guardian_name} · {item.guardian_email}
                            {item.guardian_phone ? ` · ${item.guardian_phone}` : ''}
                          </div>
                          <div className="text-[#546E7A] text-xs mt-1">
                            Attested by <span className="text-[#B0BEC5]">{item.attested_by}</span>
                            {' · '}suggested {money(item.suggested_amount)} ({item.plan})
                          </div>
                        </>
                      )}
                    </div>

                    {item.preview_url && (
                      <a href={item.preview_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-sm font-semibold text-[#00D4FF] flex-shrink-0">
                        <ExternalLink size={14} /> Preview
                      </a>
                    )}
                    {item.type === 'inquiry' && item.suggested_amount && (
                      <div className="text-[#546E7A] text-xs flex-shrink-0 whitespace-nowrap">
                        suggested {money(item.suggested_amount)}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 mt-3 pt-3 border-t border-[#546E7A]/20">
                    {item.type === 'athlete_profile' ? (
                      <>
                        <button onClick={() => recordInvoice(item)} disabled={busy}
                          className="flex items-center justify-center gap-1.5 text-xs font-semibold text-[#00D4FF] border border-[#00D4FF]/40 rounded-lg px-3 py-2.5">
                          <FileText size={13} /> Invoice sent
                        </button>
                        <button onClick={() => recordPayment(item)} disabled={busy}
                          className="flex items-center justify-center gap-1.5 text-xs font-semibold text-[#00E676] border border-[#00E676]/40 rounded-lg px-3 py-2.5">
                          <DollarSign size={13} /> Payment received
                        </button>
                        <button onClick={() => approve(item)} disabled={busy}
                          className="flex items-center justify-center gap-1.5 text-xs font-semibold bg-[#00D4FF] text-[#0A0E27] rounded-lg px-3 py-2.5">
                          <CheckCircle size={13} /> Approve &amp; publish
                        </button>
                        <button onClick={() => sendBack(item)} disabled={busy}
                          className="flex items-center justify-center gap-1.5 text-xs font-semibold text-[#FFB300] border border-[#FFB300]/40 rounded-lg px-3 py-2.5">
                          <Send size={13} /> Request changes
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setInquiryStatus(item, 'contacted')} disabled={busy}
                          className="flex items-center justify-center gap-1.5 text-xs font-semibold text-[#FFB300] border border-[#FFB300]/40 rounded-lg px-3 py-2.5">
                          <Check size={13} /> Contacted
                        </button>
                        <button onClick={() => addInquiryNote(item)} disabled={busy}
                          className="flex items-center justify-center gap-1.5 text-xs font-semibold text-[#00D4FF] border border-[#00D4FF]/40 rounded-lg px-3 py-2.5">
                          <MessageSquare size={13} /> Note
                        </button>
                        <button onClick={() => setInquiryStatus(item, 'closed')} disabled={busy}
                          className="flex items-center justify-center gap-1.5 text-xs font-semibold text-[#546E7A] border border-[#546E7A]/40 rounded-lg px-3 py-2.5">
                          <XIcon size={13} /> Close
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* ── Inquiries ─────────────────────────────────────── */}
        {tab === 'inquiries' && (
          inquiries.length === 0 ? (
            <div className="panel-card text-center py-12 text-[#546E7A]">
              No inquiries yet. They arrive from the pricing page and the program signup form.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-end">
                <button onClick={() => exportCsv(inquiries, 'inquiries')}
                  className="flex items-center gap-1.5 text-xs text-[#546E7A]">
                  <Download size={12} /> CSV
                </button>
              </div>
              {inquiries.map((q) => {
                const tone = q.status === 'new' ? '#7E57C2'
                  : q.status === 'contacted' ? '#FFB300' : '#546E7A';
                return (
                  <div key={q.id} className="panel-card"
                    style={{ borderColor: q.status === 'new' ? 'rgba(126,87,194,0.45)' : undefined,
                             opacity: q.status === 'closed' ? 0.6 : 1 }}>
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-bold">{q.name}</span>
                          <span className="text-[10px] font-bold rounded px-1.5 py-0.5 border"
                            style={{ color: tone, borderColor: tone }}>
                            {q.status.toUpperCase()}
                          </span>
                          <span className="text-[10px] font-bold rounded px-1.5 py-0.5 bg-[#243B55] text-[#B0BEC5]">
                            {q.kind.toUpperCase()}
                          </span>
                        </div>
                        {q.organization && (
                          <div className="text-[#B0BEC5] text-xs mt-1 flex items-center gap-1.5">
                            <Building2 size={11} /> {q.organization}
                            {q.state ? ` · ${q.state}` : ''}
                            {q.role ? ` · ${q.role}` : ''}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-3 mt-2">
                          <a href={`mailto:${q.email}`} className="flex items-center gap-1 text-xs">
                            <Mail size={11} /> {q.email}
                          </a>
                          {q.phone && (
                            <a href={`tel:${q.phone}`} className="flex items-center gap-1 text-xs">
                              <Phone size={11} /> {q.phone}
                            </a>
                          )}
                        </div>
                        {q.message && (
                          <p className="text-[#C8D8E8] text-sm mt-2.5 leading-relaxed whitespace-pre-line">
                            {q.message}
                          </p>
                        )}
                        {q.note && (
                          <div className="mt-2.5 rounded-lg bg-[#0A1628] border border-[#546E7A]/25 px-3 py-2">
                            <div className="text-[#546E7A] text-[10px] uppercase tracking-wide font-bold mb-0.5">Your note</div>
                            <div className="text-[#B0BEC5] text-xs">{q.note}</div>
                          </div>
                        )}
                      </div>
                      <div className="text-[#546E7A] text-xs flex-shrink-0 whitespace-nowrap">
                        {day(q.created_at)}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 mt-3 pt-3 border-t border-[#546E7A]/20">
                      {q.status !== 'contacted' && (
                        <button onClick={() => setInquiryStatus(q, 'contacted')} disabled={busy}
                          className="flex items-center justify-center gap-1.5 text-xs font-semibold text-[#FFB300] border border-[#FFB300]/40 rounded-lg px-3 py-2.5">
                          <Check size={13} /> Contacted
                        </button>
                      )}
                      {q.status !== 'closed' && (
                        <button onClick={() => setInquiryStatus(q, 'closed')} disabled={busy}
                          className="flex items-center justify-center gap-1.5 text-xs font-semibold text-[#546E7A] border border-[#546E7A]/40 rounded-lg px-3 py-2.5">
                          <XIcon size={13} /> Close
                        </button>
                      )}
                      {q.status !== 'new' && (
                        <button onClick={() => setInquiryStatus(q, 'new')} disabled={busy}
                          className="flex items-center justify-center gap-1.5 text-xs font-semibold text-[#7E57C2] border border-[#7E57C2]/40 rounded-lg px-3 py-2.5">
                          Reopen
                        </button>
                      )}
                      <button onClick={() => addInquiryNote(q)} disabled={busy}
                        className="flex items-center justify-center gap-1.5 text-xs font-semibold text-[#00D4FF] border border-[#00D4FF]/40 rounded-lg px-3 py-2.5">
                        <MessageSquare size={13} /> Note
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {/* ── Aging ─────────────────────────────────────────── */}
        {tab === 'aging' && aging && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 xs:grid-cols-5 gap-3">
              {BUCKETS.map(([key, label, color]) => (
                <Tile key={key} label={label} value={money(aging.bucket_totals[key])} color={color} />
              ))}
            </div>

            {BUCKETS.map(([key, label, color]) => (
              aging.buckets[key].length > 0 && (
                <div key={key}>
                  <h2 className="font-bold text-sm mb-2" style={{ color }}>{label} ({aging.buckets[key].length})</h2>
                  <div className="space-y-2">
                    {aging.buckets[key].map((r) => (
                      <div key={r.player_id} className="panel-card flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-white font-semibold text-sm">{r.athlete}</div>
                          <div className="text-[#546E7A] text-xs mt-0.5">
                            {r.guardian_name} · invoiced {day(r.invoiced_on)} · {r.days_outstanding}d · {r.method || '—'}
                          </div>
                          <div className="flex gap-3 mt-1.5">
                            {r.guardian_email && (
                              <a href={`mailto:${r.guardian_email}`} className="flex items-center gap-1 text-xs">
                                <Mail size={11} /> {r.guardian_email}
                              </a>
                            )}
                            {r.guardian_phone && (
                              <a href={`tel:${r.guardian_phone}`} className="flex items-center gap-1 text-xs">
                                <Phone size={11} /> {r.guardian_phone}
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="font-rajdhani font-bold text-xl flex-shrink-0" style={{ color }}>
                          {money(r.balance)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}

            {aging.past_term_in_grace.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-[#FFB300] font-bold text-sm flex items-center gap-2">
                    <AlertTriangle size={15} /> Past term — in 30-day grace ({aging.past_term_in_grace.length})
                  </h2>
                  <button onClick={() => exportCsv(aging.past_term_in_grace, 'lapsing')}
                    className="flex items-center gap-1.5 text-xs text-[#546E7A]">
                    <Download size={12} /> CSV
                  </button>
                </div>
                <div className="space-y-2">
                  {aging.past_term_in_grace.map((r) => (
                    <div key={r.player_id} className="panel-card border-[#FFB300]/40">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-white font-semibold text-sm">{r.athlete}</div>
                          <div className="text-[#546E7A] text-xs mt-0.5">
                            {r.guardian_name} · term ended {day(r.term_expired_on)} · {r.days_past_term}d ago
                          </div>
                          <div className="flex gap-3 mt-1.5">
                            {r.guardian_email && <a href={`mailto:${r.guardian_email}`} className="flex items-center gap-1 text-xs"><Mail size={11} /> {r.guardian_email}</a>}
                            {r.guardian_phone && <a href={`tel:${r.guardian_phone}`} className="flex items-center gap-1 text-xs"><Phone size={11} /> {r.guardian_phone}</a>}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="font-rajdhani font-bold text-xl" style={{ color: r.days_until_dark <= 5 ? '#EF5350' : '#FFB300' }}>
                            {r.days_until_dark}d
                          </div>
                          <div className="text-[#546E7A] text-[10px] uppercase tracking-wide">until dark</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Listings;
